import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, X, ChevronDown, ChevronRight, ChevronLeft, Check } from 'lucide-react';
import { apiClient } from '../../../lib/api';
import { useToastContext } from '../../../contexts/ToastContext';
import { formatDateOnly } from '../../../utils/dateUtils';
import { Spinner } from '../../ui/Spinner';
import { PlatformIcon, PLATFORM_LABEL } from '../../common/PlatformIcon';


interface Connection {
  platform: string;
  label: string | null;
}

interface UserRow {
  user_id: string;
  email: string | null;
  display_name: string | null;
  workspace: string | null;
  signed_up_at: string | null;
  last_sign_in_at: string | null;
  connections: Connection[];
}

const ITEMS_PER_PAGE = 20;

export const UsersListTab: React.FC = () => {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reqIdRef = useRef(0);

  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);

  const { showToast } = useToastContext();

  const fetchUsers = useCallback(async (p: number, search: string, initial = false) => {
    const reqId = ++reqIdRef.current;
    try {
      if (initial) setLoading(true);
      else setPageLoading(true);
      const result = await apiClient.getAdminUsersList(p, ITEMS_PER_PAGE, search);
      if (reqId !== reqIdRef.current) return;
      setUsers(result.users ?? []);
      setTotal(result.total ?? 0);
    } catch {
      if (reqId !== reqIdRef.current) return;
      showToast('Failed to load users', 'error');
    } finally {
      if (reqId === reqIdRef.current) {
        setLoading(false);
        setPageLoading(false);
      }
    }
  }, [showToast]);

  useEffect(() => {
    fetchUsers(0, '', true);
  }, [fetchUsers]);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      const trimmed = searchQuery.trim();
      if (trimmed === activeSearch) return;
      setActiveSearch(trimmed);
      setPage(0);
      fetchUsers(0, trimmed, false);
    }, 300);
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    fetchUsers(newPage, activeSearch, false);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setActiveSearch('');
    setPage(0);
    fetchUsers(0, '', false);
  };

  const handleExpandRow = (userId: string) => {
    setExpandedUserId(prev => prev === userId ? null : userId);
  };

  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);
  const isSearching = activeSearch.length >= 2;

  if (loading) return <Spinner size="lg" text="Loading users..." center />;

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
        <input
          id="users-list-search"
          type="text"
          placeholder="Search by email or name..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="w-full pl-9 pr-8 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-opacity-20"
          aria-label="Search users"
        />
        {searchQuery && (
          <button onClick={handleClearSearch} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600" aria-label="Clear search">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-neutral-100">
              <th className="w-8 px-4 py-3" />
              <th className="px-4 py-3 text-left text-xs font-medium text-neutral-400 uppercase tracking-wide">User</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-neutral-400 uppercase tracking-wide">Workspace</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-neutral-400 uppercase tracking-wide">Signed Up</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-neutral-400 uppercase tracking-wide">Last Login</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-neutral-400 uppercase tracking-wide">Connected</th>
            </tr>
          </thead>
          <tbody>
            {pageLoading ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center">
                <div className="flex items-center justify-center gap-2 text-neutral-400 text-sm">
                  <div className="w-4 h-4 border-2 border-neutral-200 border-t-primary-600 rounded-full animate-spin" />
                  Loading...
                </div>
              </td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-neutral-400">
                {isSearching ? 'No users match your search.' : 'No users yet.'}
              </td></tr>
            ) : users.map(user => (
              <React.Fragment key={user.user_id}>
                <tr
                  className="border-b border-neutral-50 hover:bg-neutral-50/60 cursor-pointer transition-colors"
                  onClick={() => handleExpandRow(user.user_id)}
                >
                  <td className="px-4 py-3.5 text-neutral-300">
                    {expandedUserId === user.user_id
                      ? <ChevronDown className="w-3.5 h-3.5" />
                      : <ChevronRight className="w-3.5 h-3.5" />}
                  </td>
                  {/* User */}
                  <td className="px-4 py-3.5">
                    <div className="font-medium text-neutral-900 leading-tight">
                      {user.display_name ?? user.email ?? <span className="text-neutral-400">—</span>}
                    </div>
                    {user.display_name && (
                      <div className="text-xs text-neutral-400 mt-0.5">{user.email}</div>
                    )}
                  </td>
                  {/* Workspace */}
                  <td className="px-4 py-3.5 text-neutral-600">
                    {user.workspace ?? <span className="text-neutral-300">—</span>}
                  </td>
                  {/* Signed Up */}
                  <td className="px-4 py-3.5 text-neutral-500 tabular-nums">
                    {user.signed_up_at ? formatDateOnly(user.signed_up_at) : '—'}
                  </td>
                  {/* Last Login */}
                  <td className="px-4 py-3.5 text-neutral-500 tabular-nums">
                    {user.last_sign_in_at ? formatDateOnly(user.last_sign_in_at) : '—'}
                  </td>
                  {/* Connections */}
                  <td className="px-4 py-3.5">
                    <ConnectionNames connections={user.connections} />
                  </td>
                </tr>

                {expandedUserId === user.user_id && (
                  <tr className="border-b border-neutral-100 bg-neutral-50/40">
                    <td colSpan={6} className="px-6 py-4">
                      <ConnectionDetail connections={user.connections} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>

        {/* Search truncation notice */}
        {isSearching && total > users.length && (
          <div className="px-4 py-2.5 border-t border-neutral-100 text-xs text-neutral-400">
            Showing {users.length} of {total} matches — refine your search to narrow results.
          </div>
        )}

        {/* Pagination */}
        {!isSearching && totalPages > 1 && (
          <div className="px-4 py-3 border-t border-neutral-100 flex items-center justify-between">
            <span className="text-xs text-neutral-400">
              {page * ITEMS_PER_PAGE + 1}–{Math.min((page + 1) * ITEMS_PER_PAGE, total)} of {total}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => handlePageChange(Math.max(0, page - 1))}
                disabled={page === 0}
                className="p-1.5 rounded hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed text-neutral-500"
                aria-label="Previous page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs text-neutral-500 px-2">
                {page + 1} / {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(Math.min(totalPages - 1, page + 1))}
                disabled={page >= totalPages - 1}
                className="p-1.5 rounded hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed text-neutral-500"
                aria-label="Next page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ConnectionNames: React.FC<{ connections: Connection[] }> = ({ connections }) => {
  if (connections.length === 0) return <span className="text-neutral-300">—</span>;
  return (
    <div className="flex items-center gap-1.5">
      {connections.map(c => (
        <PlatformIcon key={c.platform} platform={c.platform} size="sm" />
      ))}
    </div>
  );
};

const ConnectionDetail: React.FC<{ connections: Connection[] }> = ({ connections }) => {
  if (connections.length === 0) {
    return <p className="text-sm text-neutral-400">No integrations connected.</p>;
  }
  return (
    <div className="space-y-2">
      {connections.map(conn => (
        <div key={conn.platform} className="flex items-center gap-2.5">
          <Check className="w-3.5 h-3.5 text-success-600 flex-shrink-0" />
          <PlatformIcon platform={conn.platform} size="sm" />
          <span className="text-xs font-medium text-neutral-700">
            {PLATFORM_LABEL[conn.platform] ?? conn.platform}
          </span>
          {conn.label && (
            <span className="text-xs text-neutral-400">{conn.label}</span>
          )}
        </div>
      ))}
    </div>
  );
};
